/**
 * AssignExpertDialog Component
 * Form dialog for assigning an expert to a deal
 */

import { useState, useEffect } from 'react';
import { AssignmentFormData, ExpertType, ExpertProfile } from '@/types/partner-assignments.types';
import { EXPERT_TYPE_LABELS, PARTNER_TRANSLATIONS } from '@/constants/partner-assignments.constants';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Star, User } from 'lucide-react';

interface AssignExpertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: AssignmentFormData) => Promise<void>;
  experts: ExpertProfile[];
  dealId: string;
  buyerId?: string;
  sellerId?: string;
}

export function AssignExpertDialog({
  open,
  onOpenChange,
  onSubmit,
  experts,
  dealId,
  buyerId,
  sellerId,
}: AssignExpertDialogProps) {
  const { language } = useLanguage();
  const t = PARTNER_TRANSLATIONS[language === 'fr' ? 'fr' : 'en'];
  const lang = language === 'fr' ? 'fr' : 'en';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expertType, setExpertType] = useState<ExpertType | ''>('');
  const [selectedExpertId, setSelectedExpertId] = useState('');
  const [description, setDescription] = useState('');

  const filteredExperts = expertType 
    ? experts.filter(e => e.type === expertType)
    : experts;

  const selectedExpert = experts.find(e => e.id === selectedExpertId);

  useEffect(() => {
    if (!open) {
      setExpertType('');
      setSelectedExpertId('');
      setDescription('');
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedExpertId || !expertType) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        dealId,
        expertId: selectedExpertId,
        expertType: expertType as ExpertType,
        buyerId,
        sellerId,
        description,
      });
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t.assignExpert}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Expert Type */}
          <div className="space-y-2">
            <Label>{t.selectExpertType}</Label>
            <Select
              value={expertType}
              onValueChange={(value: ExpertType) => {
                setExpertType(value);
                setSelectedExpertId('');
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t.selectExpertType} />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(EXPERT_TYPE_LABELS) as ExpertType[]).map((type) => (
                  <SelectItem key={type} value={type}>
                    {EXPERT_TYPE_LABELS[type][lang]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Expert Selection */}
          {expertType && (
            <div className="space-y-2">
              <Label>{t.selectExpert}</Label>
              <Select
                value={selectedExpertId}
                onValueChange={setSelectedExpertId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t.selectExpert} />
                </SelectTrigger>
                <SelectContent>
                  {filteredExperts.map((expert) => (
                    <SelectItem key={expert.id} value={expert.id}>
                      <div className="flex items-center gap-2">
                        <span>{expert.name}</span>
                        {expert.rating && (
                          <span className="flex items-center text-yellow-500">
                            <Star className="w-3 h-3 fill-current" />
                            {expert.rating.toFixed(1)}
                          </span>
                        )}
                        <Badge variant="outline" className="text-xs">
                          {expert.completedAssignments} {lang === 'fr' ? 'dossiers' : 'deals'}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Selected Expert Preview */}
          {selectedExpert && (
            <div className="p-3 rounded-lg bg-muted/50 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{selectedExpert.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedExpert.company}</p>
                </div>
              </div>
              {selectedExpert.specializations && selectedExpert.specializations.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {selectedExpert.specializations.map((spec) => (
                    <Badge key={spec} variant="secondary" className="text-xs">
                      {spec}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div className="space-y-2">
            <Label>{t.description}</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={lang === 'fr' ? 'Détails de la mission...' : 'Assignment details...'}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {lang === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting || !selectedExpertId}
            >
              {isSubmitting ? '...' : t.assignExpert}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
